import { useState } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { ArrowDown, ArrowUp, ArrowUpDown, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { PartialMemory } from "~/services/memories";

type SortKey = "name" | "total_invocations" | "last_accessed_at";
type SortOrder = "asc" | "desc";

const COLUMN_LABELS: Record<SortKey, string> = {
  name: "Name",
  total_invocations: "Uses",
  last_accessed_at: "Last used",
};

// A memory no agent has loaded yet has no last-used date, so it shows the date it
// was written instead — the row still answers "how long has this been sitting
// here", which is what a zero-use memory is worth asking about.
const lastActivityAt = (memory: PartialMemory) => memory.last_accessed_at ?? memory.created_at;

// Descending is the useful default for the usage columns (most-used first) and
// the wrong one for names, which read A-Z.
const DEFAULT_ORDER: Record<SortKey, SortOrder> = {
  name: "asc",
  total_invocations: "desc",
  last_accessed_at: "desc",
};

const compare = (a: PartialMemory, b: PartialMemory, key: SortKey): number => {
  switch (key) {
    case "name":
      return a.name.localeCompare(b.name);
    case "total_invocations":
      return a.total_invocations - b.total_invocations;
    case "last_accessed_at":
      // Sorts on the date the column shows, so the order matches what is read.
      return Date.parse(lastActivityAt(a)) - Date.parse(lastActivityAt(b));
  }
};

interface MemoryTableProps {
  memories: PartialMemory[];
  onOpen: (memory: PartialMemory) => void;
  onDelete: (memory: PartialMemory) => void;
}

/**
 * The user's memories, one row each, sortable by name or by how much the agents
 * actually use them. A row opens the memory's full text; the trash action
 * deletes it. Nothing here is editable — memories are written by agents.
 */
export const MemoryTable = ({ memories, onOpen, onDelete }: MemoryTableProps) => {
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const handleSortClick = (key: SortKey) => {
    if (key === sortKey) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder(DEFAULT_ORDER[key]);
    }
  };

  const sorted = [...memories].sort(
    (a, b) => compare(a, b, sortKey) * (sortOrder === "asc" ? 1 : -1)
  );

  // The cell carries the sort state, not just the button, so the arrow icon is
  // not the only thing saying which column is sorted and which way.
  const sortableHeader = (key: SortKey, className?: string) => (
    <TableHead
      className={className}
      aria-sort={sortKey !== key ? "none" : sortOrder === "asc" ? "ascending" : "descending"}
    >
      <button
        type="button"
        onClick={() => handleSortClick(key)}
        className="inline-flex cursor-pointer items-center gap-1 font-medium transition-colors hover:text-foreground"
      >
        {COLUMN_LABELS[key]}
        {sortKey !== key ? (
          <ArrowUpDown className="size-3.5 text-muted-foreground/50" aria-hidden="true" />
        ) : sortOrder === "asc" ? (
          <ArrowUp className="size-3.5" aria-hidden="true" />
        ) : (
          <ArrowDown className="size-3.5" aria-hidden="true" />
        )}
      </button>
    </TableHead>
  );

  return (
    // Fixed layout so the sized columns hold and the memory column takes the rest.
    // Under auto layout the truncated name and description widen the table instead
    // of ellipsizing, which is what puts a scrollbar under a four-column table.
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          {sortableHeader("name")}
          {sortableHeader("total_invocations", "w-20 text-right")}
          {sortableHeader("last_accessed_at", "w-32 text-right")}
          <TableHead className="w-10">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((memory) => (
          <TableRow key={memory.id} className="group cursor-pointer" onClick={() => onOpen(memory)}>
            <TableCell>
              <span className="block truncate text-sm font-medium">{memory.name}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {memory.description}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <span
                className={cn(
                  "text-[13px] tabular-nums",
                  memory.total_invocations > 0 ? "text-foreground" : "text-muted-foreground/50"
                )}
              >
                {memory.total_invocations}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      "text-[13px]",
                      memory.last_accessed_at ? "text-muted-foreground" : "text-muted-foreground/50"
                    )}
                  >
                    {formatDistanceToNowStrict(new Date(lastActivityAt(memory)), {
                      addSuffix: true,
                    })}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {memory.last_accessed_at
                    ? `Last used ${new Date(memory.last_accessed_at).toLocaleString()}`
                    : `Never used. Written ${new Date(memory.created_at).toLocaleString()}`}
                </TooltipContent>
              </Tooltip>
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => onDelete(memory)}
                aria-label={`Delete ${memory.name}`}
                className="flex cursor-pointer items-center text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
              >
                <Trash2 className="size-4" />
              </button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
